import { useState, useCallback } from 'react';
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Stack,
	Switch,
	Flex,
	Text,
	useToast,
	Select,
} from '@chakra-ui/react';
import axios from 'axios';
import config from '../config';
import globalStore from '../store';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { formatTimestamp } from '../utility';

export function JsonDiffCheckerModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
	const globalData = globalStore();
	const toast = useToast();

	const [id1, setId1] = useState('');
	const [id2, setId2] = useState('');
	const [name1, setName1] = useState('');
	const [name2, setName2] = useState('');
	const [originalJson, setOriginalJson] = useState<any>(null);
	const [comparedJson, setComparedJson] = useState<any>(null);
	const [loading, setLoading] = useState(false);
	const [fetchingSpecialData, setFetchingSpecialData] = useState(false);
	const [showOnlyDiff, setShowOnlyDiff] = useState(true);
	const [sameKeyConfigs, setSameKeyConfigs] = useState<any[]>([]);

	const fetchRemoteConfigValue = useCallback(
		async (id: string) => {
			const response = await axios.get(`${config.API_ENDPOINT}/game/${globalData.gameId}/rc_package/1/rc_value/${id}`, {
				headers: { Authorization: `Bearer ${globalData.JWTToken}` },
			});
			const rc = response.data;
			return rc;
		},
		[globalData.gameId, globalData.JWTToken],
	);

	const fetchSpecialData = useCallback(async () => {
		if (!id1) {
			toast({
				title: 'Missing ID',
				description: 'Please enter Config ID #1 first.',
				status: 'warning',
				duration: 3000,
				isClosable: true,
				position: 'top',
			});
			return;
		}

		setFetchingSpecialData(true);
		try {
			const response = await axios.get(`${config.API_ENDPOINT}/game/${globalData.gameId}/rc_package/1/rc_value/last_10_same_key/${id1}`, {
				headers: { Authorization: `Bearer ${globalData.JWTToken}` },
			});

			// Handle the response data as needed
			console.log('Special data fetched:', response.data);

			// Set the same key configs for dropdown
			setSameKeyConfigs(response.data || []);

			// Clear the second input if configs are found
			if (response.data && response.data.length > 0) {
				setId2('');
			}

			toast({
				title: 'Success',
				description: `Found ${response.data?.length || 0} configs with the same key.`,
				status: 'success',
				duration: 3000,
				isClosable: true,
				position: 'top',
			});
		} catch (err) {
			console.error('Error fetching special data:', err);
			toast({
				title: 'Fetch Error',
				description: 'Could not fetch special data.',
				status: 'error',
				duration: 4000,
				isClosable: true,
				position: 'top',
			});
		}
		setFetchingSpecialData(false);
	}, [id1, globalData.gameId, globalData.JWTToken, toast]);

	const fetchConfigs = useCallback(async () => {
		if (!id1 || !id2) return;

		setLoading(true);

		try {
			const [json1, json2] = await Promise.all([fetchRemoteConfigValue(id1), fetchRemoteConfigValue(id2)]);

			setOriginalJson(json1);
			setComparedJson(json2);
			setName1(json1.key || '');
			setName2(json2.key || '');

			// Check for differences after fetching the configs
			const formatValue = (value: any) => {
				if (typeof value === 'string') {
					try {
						const parsed = JSON.parse(value);
						return JSON.stringify(parsed, null, 2);
					} catch {
						return value;
					}
				}
				return JSON.stringify(value, null, 2);
			};

			const original = formatValue(json1.value);
			const compared = formatValue(json2.value);

			// Only show toast when comparison is complete
			if (original === compared) {
				toast({
					title: 'No Differences Found',
					description: 'The two configs have identical values.',
					status: 'info',
					duration: 4000,
					isClosable: true,
					position: 'top',
				});
			}
		} catch (err) {
			console.error(err);
			toast({
				title: 'Fetch Error',
				description: 'Could not fetch one or both remote configs.',
				status: 'error',
				duration: 4000,
				isClosable: true,
				position: 'top',
			});
			setOriginalJson(null);
			setComparedJson(null);
		}
		setLoading(false);
	}, [id1, id2, fetchRemoteConfigValue, toast]);

	const renderReactDiffViewer = useCallback(() => {
		if (!originalJson || !comparedJson) return null;

		let original: string;
		let compared: string;

		// Helper function to safely format value
		const formatValue = (value: any) => {
			if (typeof value === 'string') {
				// Try to parse as JSON first
				try {
					const parsed = JSON.parse(value);
					return JSON.stringify(parsed, null, 2);
				} catch {
					// If it's not valid JSON, return as plain string
					return value;
				}
			}
			// If it's already an object, stringify it
			return JSON.stringify(value, null, 2);
		};

		try {
			original = formatValue(originalJson.value);
			compared = formatValue(comparedJson.value);
		} catch (error) {
			toast({
				title: 'Parse Error',
				description: 'Error formatting one of the config values.',
				status: 'error',
				duration: 5000,
				isClosable: true,
				position: 'top',
			});
			return (
				<Box mt={4} p={4} bg="red.100" borderRadius="md" color="red.700">
					Error formatting config values.
				</Box>
			);
		}

		return (
			<Box mt={6} maxHeight="600px" overflow="auto" borderRadius="md" borderWidth="1px">
				<ReactDiffViewer oldValue={original} newValue={compared} splitView={true} showDiffOnly={showOnlyDiff} useDarkTheme={true} />
			</Box>
		);
	}, [originalJson, comparedJson, showOnlyDiff, toast]);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="6xl">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Remote Config JSON Diff Checker</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Stack spacing={4}>
						<FormControl>
							<FormLabel>Remote Config ID #1</FormLabel>
							<Flex gap={2}>
								<Input value={id1} onChange={(e) => setId1(e.target.value)} flex={1} />
								<Button colorScheme="purple" onClick={fetchSpecialData} isLoading={fetchingSpecialData} size="md" minW="100px">
									Fetch Configs With Same Key
								</Button>
							</Flex>
						</FormControl>
						<FormControl>
							<FormLabel>Remote Config ID #2</FormLabel>
							{sameKeyConfigs.length > 0 ? (
								<Select placeholder="Select a config to compare" value={id2} onChange={(e) => setId2(e.target.value)}>
									{sameKeyConfigs.map((config) => (
										<option key={config.id} value={config.id}>
											{config.id} - {config.key} ({config.isTest ? 'Development' : 'Production'}) {formatTimestamp(config.lastUpdated)}
										</option>
									))}
								</Select>
							) : (
								<Input value={id2} onChange={(e) => setId2(e.target.value)} />
							)}
						</FormControl>
						<Button colorScheme="blue" onClick={fetchConfigs} isLoading={loading}>
							Compare
						</Button>

						<Flex align="center" gap={2}>
							<Switch isChecked={showOnlyDiff} onChange={() => setShowOnlyDiff(!showOnlyDiff)} colorScheme="teal" />
							<Text fontSize="sm" whiteSpace="nowrap">
								Show Only Differences
							</Text>
						</Flex>

						{originalJson && comparedJson && (
							<Box mt={6}>
								<Stack direction={{ base: 'column', md: 'row' }} spacing={4} maxWidth={'100%'}>
									<Box flex={1}>
										<FormLabel>
											Config ID: {id1} - {name1}
										</FormLabel>
									</Box>
									<Box flex={1}>
										<FormLabel>
											Config ID: {id2} - {name2}
										</FormLabel>
									</Box>
								</Stack>
							</Box>
						)}

						{renderReactDiffViewer()}
					</Stack>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}

export default JsonDiffCheckerModal;
